import { css } from '@emotion/core';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from '@material-ui/core';
import { ArrowBack, ArrowForward } from '@material-ui/icons';
import React, { useEffect } from 'react';
import { ContactData } from 'shared/models/contact';
import { PersonFile } from 'shared/models/person';
import { ConflictProfileWithDetails } from 'shared/types/conflict-profiles';

import {
    getProfileConflictDetails,
    reassignContact as reassignContactData,
    reassignFile,
    reassignProfileUrl,
    resolveProfileConflict
} from '../api';
import { logger } from '../common/logger';
import { FileDownloadLink } from '../core-ui/file-download-link';
import { LoadingModal } from '../core-ui/loading-modal';
import { Spinner } from '../core-ui/spinner';
import { useModal } from '../hooks/use-modal';

const dialogStyles = (theme: Theme) => css`
    .data-section {
        display: flex;
        align-items: stretch;

        .profile-left,
        .profile-right {
            width: 50%;
            overflow: hidden;
        }

        .profile-left {
            padding-right: 10px;
            border-right: thin solid ${theme.palette.divider};
        }

        .profile-right {
            padding-left: 10px;
        }
    }

    .transfer {
        display: flex;
        align-items: center;
        justify-content: flex-end;

        &.right {
            flex-direction: row;
        }

        &.left {
            flex-direction: row-reverse;
        }

        .MuiIconButton-root {
            margin: 0 5px;
        }
    }

    .data-section.profile-name {
        .transfer {
            font-size: 20px;
            padding: 10px 56px;
            border-bottom: thin solid ${theme.palette.divider};
        }
    }
`;

/** Transfers selected data between two existing people, with or without a profile conflict. */
export const ProfileDataTransfer: React.FC<{
    personId1: string;
    personId2: string;
    profiles?: ConflictProfileWithDetails;
    readOnly?: boolean;
    onClose: () => void;
    onResolved?: () => void;
}> = (props) => {
    const theme = useTheme();
    const { getConfirmation } = useModal();
    const [loadedProfiles, setProfiles] = React.useState<ConflictProfileWithDetails>(null);
    const profiles = props.profiles || loadedProfiles;
    const [error, setError] = React.useState<Error>();
    const [loadingModal, setLoadingModal] = React.useState(false);
    const readOnly = props.readOnly || profiles?.resolution.processing;
    const handleClose = () => {
        if (!loadingModal) props.onClose();
    };

    useEffect(() => {
        if (props.profiles) return;
        let cancelled = false;
        getProfileConflictDetails(props.personId1, props.personId2)
            .then((result) => {
                if (!result.success) throw new Error(result.data?.message || 'Unable to load profiles');
                if (result.data.resolution.mergedPersonId) throw new Error('A profile has been merged');
                if (!cancelled) setProfiles(result.data);
            })
            .catch((err: Error) => {
                if (!cancelled) setError(err);
            });
        return () => {
            cancelled = true;
        };
    }, [props.personId1, props.personId2, props.profiles]);

    const handleResolveConflict = async () => {
        setLoadingModal(true);
        try {
            const result = await resolveProfileConflict(props.personId1, props.personId2);
            if (!result.success) throw new Error(result.data?.message || 'Unable to resolve conflict');
            if (props.onResolved) props.onResolved();
            else props.onClose();
        } catch (err) {
            setError(err);
        } finally {
            setLoadingModal(false);
        }
    };

    const reassignUrl = (url: string, toPersonId: string) => () => {
        getConfirmation(
            () => reassignUrlConfirmed(url, toPersonId),
            'The profile url will be transferred to the other profile',
            'Please confirm'
        );
    };

    const reassignUrlConfirmed = (url: string, toPersonId: string) => {
        setLoadingModal(true);
        reassignProfileUrl(url, toPersonId)
            .then((result) => {
                if (!result.success) throw new Error(result.data?.message || 'Unable to transfer data');
                const { personIds, details } = profiles;
                const left = personIds[0];
                const right = personIds[1];
                const from = toPersonId === left ? right : left;
                const target = details[from].profileUrls.find((u) => u.url === url);
                details[from].profileUrls = details[from].profileUrls.filter((u) => u.url !== url);
                details[toPersonId].profileUrls.push(target);
            })
            .catch((err: Error) => {
                setError(err);
                logger.error(err, { message: 'Error reassign profile urls' });
            })
            .finally(() => {
                setLoadingModal(false);
            });
    };

    const reassignContact = (contact: ContactData, toPersonId: string) => () => {
        const associatedRecordsText =
            contact.channel === 'email'
                ? 'email messages and notes for related jobs'
                : 'phone calls, text messages and notes for related jobs';
        getConfirmation(
            () => reassignContactConfirmed(contact, toPersonId),
            `The ${contact.channel} will be transferred to the other profile, ` +
                `along with associated ${associatedRecordsText}`,
            'Please confirm'
        );
    };

    const reassignContactConfirmed = (contact: ContactData, toPersonId: string) => {
        setLoadingModal(true);
        reassignContactData(contact, toPersonId)
            .then((result) => {
                if (!result.success) throw new Error(result.data?.message || 'Unable to transfer data');
                const { personIds, details } = profiles;
                const left = personIds[0];
                const right = personIds[1];
                const from = toPersonId === left ? right : left;
                details[from].contacts = details[from].contacts.filter((u) => u.value !== contact.value);
                details[toPersonId].contacts.push({ ...contact, personId: toPersonId });
            })
            .catch((err: Error) => {
                setError(err);
                logger.error(err, { message: 'Error reassigning contact' });
            })
            .finally(() => {
                setLoadingModal(false);
            });
    };

    const handleTransferFile = (file: PersonFile, fromPersonId: string, toPersonId: string) => () => {
        getConfirmation(
            async () => {
                setLoadingModal(true);
                try {
                    const result = await reassignFile(file.path, fromPersonId, toPersonId);
                    if (!result.success) throw new Error(result.data?.message || 'Unable to transfer file');
                    const { details } = profiles;
                    details[fromPersonId].person.files = details[fromPersonId].person.files.filter(
                        (f) => f.path !== file.path
                    );
                    const targetFiles = details[toPersonId].person.files || [];
                    if (!targetFiles.some((f) => f.path === file.path)) targetFiles.push(file);
                    details[toPersonId].person.files = targetFiles;
                } catch (err) {
                    setError(err);
                    logger.error(err, { message: 'Error transferring profile file' });
                } finally {
                    setLoadingModal(false);
                }
            },
            `${file.name} will be transferred to the other profile`,
            'Please confirm'
        );
    };

    const generateProfileUrls = (urls: string[], toPersonId: string, direction: 'left' | 'right') => {
        const icon = direction === 'left' ? <ArrowBack /> : <ArrowForward />;
        const urlElements = urls.map((u) => {
            return (
                <div className={`transfer ${direction}`} key={u}>
                    <a href={`https://${u}`} target="_blank">
                        {u}
                    </a>
                    <Tooltip title="Reassign url to the other profile">
                        <IconButton disabled={readOnly || loadingModal} onClick={reassignUrl(u, toPersonId)}>
                            {icon}
                        </IconButton>
                    </Tooltip>
                </div>
            );
        });
        return urls && urls.length > 0 ? urlElements : undefined;
    };

    const generateContacts = (contacts: ContactData[], toPersonId: string, direction: 'left' | 'right') => {
        const icon = direction === 'left' ? <ArrowBack /> : <ArrowForward />;
        const contactElements = contacts.map((u) => {
            return (
                <div className={`transfer ${direction}`} key={u.value}>
                    {u.value}
                    <Tooltip title="Reassign contact to the other profile">
                        <IconButton disabled={readOnly || loadingModal} onClick={reassignContact(u, toPersonId)}>
                            {icon}
                        </IconButton>
                    </Tooltip>
                </div>
            );
        });
        return contacts && contacts.length > 0 ? contactElements : undefined;
    };

    const generateFiles = (personId: string, toPersonId: string, direction: 'left' | 'right') => {
        const icon = direction === 'left' ? <ArrowBack /> : <ArrowForward />;
        return profiles.details[personId].person.files?.map((file) => {
            const url = `media/files/${file.path}`;
            return (
                <div className={`transfer ${direction}`} key={file.path}>
                    <FileDownloadLink path={url} filename={file.name}>
                        <a href={url} target="_blank">
                            {file.name}
                        </a>
                    </FileDownloadLink>
                    <Tooltip title="Transfer file to the other profile">
                        <IconButton
                            disabled={readOnly || loadingModal}
                            onClick={handleTransferFile(file, personId, toPersonId)}
                        >
                            {icon}
                        </IconButton>
                    </Tooltip>
                </div>
            );
        });
    };

    const resolveButton = profiles?.resolution.conflictOpen ? (
        <Button disabled={readOnly || loadingModal} onClick={handleResolveConflict}>
            Resolve conflict
        </Button>
    ) : null;
    let content = error ? <Typography color="error">{error.message}</Typography> : <Spinner />;
    if (profiles) {
        const { personIds, details } = profiles;
        const [left, right] = personIds;
        const leftProfileUrls = details[left].profileUrls.map((u) => u.url);
        const leftProfileUrlsSection = generateProfileUrls(leftProfileUrls, right, 'right');
        const leftContacts = details[left].contacts;
        const leftContactsSection = generateContacts(leftContacts, right, 'right');
        const rightProfileUrls = details[right].profileUrls.map((u) => u.url);
        const rightProfileUrlsSection = generateProfileUrls(rightProfileUrls, left, 'left');
        const rightContacts = details[right].contacts;
        const rightContactsSection = generateContacts(rightContacts, left, 'left');
        const leftFilesSection = generateFiles(left, right, 'right');
        const rightFilesSection = generateFiles(right, left, 'left');

        content = (
            <>
                {error ? <Typography color="error">{error.message}</Typography> : null}
                <div className="data-section profile-name">
                    <div className="profile-left">
                        <div className="transfer right">
                            <a href={`/person/${left}`} target="_blank">
                                {details[left].person.name.full}
                            </a>
                        </div>
                    </div>
                    <div className="profile-right">
                        <div className="transfer left">
                            <a href={`/person/${right}`} target="_blank">
                                {details[right].person.name.full}
                            </a>
                        </div>
                    </div>
                </div>
                <div className="data-section">
                    <div className="profile-left">{leftProfileUrlsSection}</div>
                    <div className="profile-right">{rightProfileUrlsSection}</div>
                </div>
                <div className="data-section">
                    <div className="profile-left">{leftContactsSection}</div>
                    <div className="profile-right">{rightContactsSection}</div>
                </div>
                <div className="data-section">
                    <div className="profile-left">{leftFilesSection}</div>
                    <div className="profile-right">{rightFilesSection}</div>
                </div>
            </>
        );
    }
    return (
        <Dialog open={true} onClose={handleClose} fullWidth={true} maxWidth="md" css={dialogStyles(theme)}>
            <DialogTitle>
                <Typography variant="h4" component="div">
                    Transfer profile data
                </Typography>
            </DialogTitle>
            <DialogContent>
                {loadingModal ? <LoadingModal /> : null}
                {content}
            </DialogContent>
            <DialogActions>
                <Button disabled={loadingModal} onClick={handleClose}>
                    {profiles?.resolution.conflictOpen ? 'Back' : 'Done'}
                </Button>
                {resolveButton}
            </DialogActions>
        </Dialog>
    );
};
